import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Switch } from "../../../../components/ui/switch";
import { Textarea } from "../../../../components/ui/textarea";
import { Input } from "../../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { cn } from "../../../../lib/utils";
import DomainListInput from "../../../../shared/forms/DomainListInput";
import {
  useEmailSettings,
  useUpdateEmailSettings,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import type { AccountCreationThreshold, EmailSettings } from "../../../../services/emailSettingsEndpoint";

const THRESHOLD_OPTIONS: Array<{ value: AccountCreationThreshold; label: string; hint: string }> = [
  { value: "first_message", label: "First message", hint: "Create an account on the first inbound email" },
  { value: "two_way", label: "Two-way conversation", hint: "Only create after a reply (two-way thread)" },
];

const SUBSCRIPTION_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "Subscribed", label: "Subscribed" },
  { value: "Trial", label: "Trial" },
  { value: "Churned", label: "Churned" },
];

// Sentinel values for the subscription-status select (Radix Select cannot use empty string).
const SUB_STATUS_NONE = "__none__";
const SUB_STATUS_OTHER = "__other__";

const EmailSettingsPage = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;

  const emailSettingsQuery = useEmailSettings({ workspaceId });
  const settings = emailSettingsQuery.data as EmailSettings | undefined;
  const isLoading = emailSettingsQuery.isLoading;
  const updateMutation = useUpdateEmailSettings();

  const [createFromEmail, setCreateFromEmail] = useState(false);
  const [classifierInstructions, setClassifierInstructions] = useState("");
  const [disallowList, setDisallowList] = useState<string[]>([]);
  const [allowList, setAllowList] = useState<string[]>([]);
  const [internalDomains, setInternalDomains] = useState<string[]>([]);
  const [threshold, setThreshold] = useState<string>("first_message");
  // Subscription status: the dropdown choice (a known value, NONE, or OTHER) + the free-text value when OTHER.
  const [subStatusChoice, setSubStatusChoice] = useState<string>(SUB_STATUS_NONE);
  const [customSubStatus, setCustomSubStatus] = useState<string>("");

  useEffect(() => {
    if (!settings) return;
    setCreateFromEmail(Boolean(settings.create_from_email));
    setClassifierInstructions(settings.classifier_instructions ?? "");
    setDisallowList(settings.domain_disallow_list ?? []);
    setAllowList(settings.domain_allow_list ?? []);
    setInternalDomains(settings.internal_domains ?? []);
    setThreshold(settings.account_creation_threshold || "first_message");

    const subStatus = settings.default_subscription_status;
    if (!subStatus) {
      setSubStatusChoice(SUB_STATUS_NONE);
      setCustomSubStatus("");
    } else if (SUBSCRIPTION_STATUS_OPTIONS.some((option) => option.value === subStatus)) {
      setSubStatusChoice(subStatus);
      setCustomSubStatus("");
    } else {
      setSubStatusChoice(SUB_STATUS_OTHER);
      setCustomSubStatus(subStatus);
    }
  }, [settings]);

  const saving = updateMutation.isLoading;

  const handleSave = () => {
    if (!workspaceId) return;
    let defaultSubscriptionStatus: string | null;
    if (subStatusChoice === SUB_STATUS_NONE) {
      defaultSubscriptionStatus = null;
    } else if (subStatusChoice === SUB_STATUS_OTHER) {
      defaultSubscriptionStatus = customSubStatus.trim() || null;
    } else {
      defaultSubscriptionStatus = subStatusChoice;
    }
    updateMutation.mutate({
      workspace_id: workspaceId,
      create_from_email: createFromEmail,
      classifier_instructions: classifierInstructions.trim() ? classifierInstructions : null,
      domain_disallow_list: disallowList,
      domain_allow_list: allowList,
      internal_domains: internalDomains,
      account_creation_threshold: threshold,
      default_subscription_status: defaultSubscriptionStatus,
    });
  };

  const selectedThreshold = useMemo(
    () => THRESHOLD_OPTIONS.find((option) => option.value === threshold),
    [threshold],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/25 border-t-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium text-foreground">Email Settings</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Control how accounts and contacts are created from inbound email.
        </p>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-foreground">Create accounts from email</p>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            When enabled, inbound emails from unknown domains can automatically create accounts and contacts.
          </p>
        </div>
        <Switch
          checked={createFromEmail}
          onCheckedChange={setCreateFromEmail}
          disabled={saving}
        />
      </div>

      <div className={cn("space-y-6", !createFromEmail && "pointer-events-none opacity-50")}>
        <div className="space-y-2">
          <p className="text-[13px] font-medium text-foreground">Classifier instructions</p>
          <p className="text-[12px] text-muted-foreground">
            Guidance for the AI classifier on which domains count as customers (optional).
          </p>
          <Textarea
            value={classifierInstructions}
            onChange={(event) => setClassifierInstructions(event.target.value)}
            disabled={saving}
            placeholder="e.g. Treat domains of paying customers as accounts. Ignore vendors, recruiters, and personal email providers."
            className="min-h-[100px] text-[13px]"
          />
        </div>

        <DomainListInput
          label="Internal domains"
          description="Your own company domains. Email between internal addresses is never used to create accounts."
          placeholder="acme.com"
          values={internalDomains}
          onChange={setInternalDomains}
          disabled={saving}
        />

        <DomainListInput
          label="Allow list"
          description="Domains that should always create accounts, skipping classification."
          placeholder="customer.com"
          values={allowList}
          onChange={setAllowList}
          disabled={saving}
        />

        <DomainListInput
          label="Disallow list"
          description="Domains that should never create accounts (e.g. vendors, personal email providers)."
          placeholder="gmail.com"
          values={disallowList}
          onChange={setDisallowList}
          disabled={saving}
        />

        <div className="space-y-2">
          <p className="text-[13px] font-medium text-foreground">Account creation threshold</p>
          <p className="text-[12px] text-muted-foreground">
            {selectedThreshold?.hint || "When an account should be created from a conversation."}
          </p>
          <Select value={threshold} onValueChange={setThreshold} disabled={saving}>
            <SelectTrigger className="h-9 w-[260px] text-[13px]">
              <SelectValue placeholder="Select threshold" />
            </SelectTrigger>
            <SelectContent>
              {THRESHOLD_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-[13px]">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-medium text-foreground">Default subscription status for auto-created accounts</p>
          <p className="text-[12px] text-muted-foreground">
            Accounts created from email get this subscription status. Leave as &quot;None&quot; for no default.
          </p>
          <Select value={subStatusChoice} onValueChange={setSubStatusChoice} disabled={saving}>
            <SelectTrigger className="h-9 w-[260px] text-[13px]">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SUB_STATUS_NONE} className="text-[13px]">None</SelectItem>
              {SUBSCRIPTION_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-[13px]">
                  {option.label}
                </SelectItem>
              ))}
              <SelectItem value={SUB_STATUS_OTHER} className="text-[13px]">Other…</SelectItem>
            </SelectContent>
          </Select>
          {subStatusChoice === SUB_STATUS_OTHER && (
            <Input
              value={customSubStatus}
              onChange={(event) => setCustomSubStatus(event.target.value)}
              disabled={saving}
              placeholder="Enter a custom subscription status"
              className="h-9 w-[260px] text-[13px]"
            />
          )}
        </div>
      </div>

      <div className="flex justify-end border-t border-border pt-4">
        <Button size="sm" className="h-8 text-[12px]" onClick={handleSave} disabled={saving || !workspaceId}>
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default EmailSettingsPage;
